/**
 * Typed API errors. The backend's error code is preserved verbatim in `code`; the client adds only two
 * codes of its own for failures that never reached an API handler.
 */

export const API_ERROR_CODES = {
  /** Client-side: the request never completed (offline, DNS, CORS, connection reset). */
  NETWORK_ERROR: 'NETWORK_ERROR',
  /** Client-side: a 2xx response that is not the `{ data }` envelope. */
  INVALID_RESPONSE: 'INVALID_RESPONSE',
  /** Backend codes the frontend branches on. Others are carried through unchanged. */
  UNAUTHORIZED: 'UNAUTHORIZED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  CART_REVISION_CONFLICT: 'CART_REVISION_CONFLICT',
  CART_EMPTY: 'CART_EMPTY',
  DUPLICATE_IDEMPOTENCY_KEY: 'DUPLICATE_IDEMPOTENCY_KEY',
  ITEM_OUT_OF_STOCK: 'ITEM_OUT_OF_STOCK',
  SESSION_ALREADY_CLOSED: 'SESSION_ALREADY_CLOSED',
  INVALID_QR_TOKEN: 'INVALID_QR_TOKEN',
} as const;

export interface ApiErrorInit {
  status: number;
  code: string;
  message: string;
  details?: Record<string, unknown>;
  correlationId?: string;
  timestamp?: string;
}

export class ApiError extends Error {
  /** HTTP status. `0` when the request never completed. */
  readonly status: number;
  /** The backend's `error.code`, or `NETWORK_ERROR`, `INVALID_RESPONSE`, `HTTP_<status>`. */
  readonly code: string;
  readonly details?: Record<string, unknown>;
  readonly correlationId?: string;
  readonly timestamp?: string;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.correlationId = init.correlationId;
    this.timestamp = init.timestamp;
  }

  /**
   * True when the envelope code, or a code carried in `details.code`, equals `code`. The backend reports
   * an unknown QR token as `NOT_FOUND` with `details.code = "INVALID_QR_TOKEN"` (the API specification
   * lists it as a top-level code), so callers check with this method instead of comparing `code`.
   */
  hasCode(code: string): boolean {
    return this.code === code || this.details?.code === code;
  }

  get isNetworkError(): boolean {
    return this.code === API_ERROR_CODES.NETWORK_ERROR;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export interface CartRevisionConflict {
  /** The cart's current revision when the server reported it. */
  currentRevision: number | null;
}

/**
 * Recognises `409 CART_REVISION_CONFLICT` (a cart changed after the customer reviewed it). Returns null
 * for every other error, so callers can show the friendly "Your cart was updated" state only here.
 */
export function asCartRevisionConflict(error: unknown): CartRevisionConflict | null {
  if (
    !isApiError(error) ||
    error.status !== 409 ||
    !error.hasCode(API_ERROR_CODES.CART_REVISION_CONFLICT)
  ) {
    return null;
  }
  const current = error.details?.current_revision;
  return { currentRevision: typeof current === 'number' ? current : null };
}
